import {computed, inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {BehaviorSubject, Observable, of, throwError} from 'rxjs';
import {catchError, map, shareReplay, tap} from 'rxjs/operators';
import type {LoginRequest, User, UserResponse} from '../models/auth.types';
import {Logger} from '@app/utils';
import {toSignal} from '@angular/core/rxjs-interop';

const logger: Logger = Logger.get('AuthService');
const BE_ROUTES = {
  INIT_AUTH: '/api/v2/initialize-app',
  LOGIN: '/api/v2/login',
  AUTHENTICATE_USER: '/api/v2/authenticate-user',
  REFRESH_TOKEN: '/api/v2/refresh-token',
  LOGOUT: '/api/v2/logout',
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  readonly #router = inject(Router);
  readonly #httpClient = inject(HttpClient);

  readonly #currentUser$ = new BehaviorSubject<User | null>(null);

  public readonly currentUser = toSignal<User | null>(this.#currentUser$);

  public readonly isAdminUser = computed<boolean>(() => {
    const userRole = this.currentUser()?.role;
    logger.debug(`Check role: ${userRole}`);
    return userRole === 'admin';
  });

  public get currentAuthenticatedUser(): User | null {
    return this.#currentUser$.value;
  }

  public initializeAuth(): Observable<void | null> {
    logger.debug('Initializing authentication...');

    return this.#httpClient.get<void>(BE_ROUTES.INIT_AUTH).pipe(
      catchError(error => {
        logger.error('Authentication initialization failed:', error);
        return of(null);
      }),
      shareReplay(1)
    );
  }

  public login(authenticateRequest?: LoginRequest): Observable<User> {
    if (this.currentAuthenticatedUser) {
      logger.debug('User already authenticated');
      return of(this.currentAuthenticatedUser);
    }

    if (!authenticateRequest) {
      return throwError(() => new Error('Login credentials are required'));
    }

    logger.debug('Attempting to login user...');

    return this.#httpClient.post<UserResponse>(BE_ROUTES.LOGIN, authenticateRequest).pipe(
      map(userResponse => this.#mapResponseToUser(userResponse)),
      tap(mappedUser => this.#currentUser$.next(mappedUser))
    );
  }

  public authenticateUser(): Observable<User> {
    logger.debug('Attempting to authenticate user...');

    return this.#httpClient.get<UserResponse>(BE_ROUTES.AUTHENTICATE_USER).pipe(
      // If the request succeeds, the user is authenticated
      map(response => {
        let user = this.#mapResponseToUser(response);
        this.#currentUser$.next(user);
        return user;
      }),
      catchError(error => {
        logger.error('Failed to authenticate user:', error);
        // Clear current user on auth failure
        this.#currentUser$.next(null);
        return throwError(() => error);
      })
    );
  }

  public refreshToken(): Observable<boolean> {
    logger.debug('Attempting to refresh token...');

    return this.#httpClient.post<{ success: boolean }>(BE_ROUTES.REFRESH_TOKEN, null).pipe(
      map(response => response.success),
      catchError(error => {
        logger.error('Token refresh failed:', error);
        this.#currentUser$.next(null);
        return throwError(() => error);
      })
    );
  }

  public async logout(): Promise<void> {
    this.#httpClient.post(BE_ROUTES.LOGOUT, null).subscribe();

    this.#currentUser$.next(null);
    await this.#router.navigate(['/login']);
  }

  public get isLoggedIn(): boolean {
    return !!this.currentAuthenticatedUser;
  }

  #mapResponseToUser(userResponse: UserResponse): User {
    return {
      username: userResponse.username,
      operator: userResponse.operator,
      role: userResponse.role,
    };
  }
}
